import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { FONT } from '../../styles/emotion';
import COLORS, { COLORS_BUTTONS, COLORS_SEMANTIC } from '../../styles/emotion/colors3';
import type { ButtonColorway, ButtonSize } from './ButtonV3';

export type ButtonComponent = 'button' | 'a';
export type ColorwayEmotion = Uppercase<ButtonColorway>;

interface ButtonProps {
  variant?: string;
  compact?: boolean;
  disabled?: boolean;
  bordered?: boolean;
  fullWidth?: boolean;
  role?: string;
  colorway: ButtonColorway;
  size?: ButtonSize;
}

/**
 * Get the background colorway to use with the base button colorway.
 * @param colorway Base colorway of the button
 * @returns The colorway used in the background
 */
export const getComplementaryColorway = (colorway: ButtonColorway): string => {
  switch (colorway) {
    case 'forest':
      return 'mint';
    case 'mint':
      return 'forest';
    case 'wine':
      return 'lilac';
    case 'lilac':
      return 'wine';
    case 'rose':
      return 'blush';
    case 'blush':
      return 'rose';
    case 'marine':
      return 'sky';
    case 'sky':
      return 'marine';
    case 'bay':
      return 'cloud';
    case 'cloud':
      return 'bay';
    case 'black':
    default:
      return 'white';
  }
};

/**
 * Get the disabled color for a given colorway
 * @param colorway Button colorway
 * @returns The disabled button color
 */
const getDisabledColorway = (colorway: ButtonColorway): string => {
  switch (colorway) {
    case 'forest':
      return 'FOREST_050';
    case 'mint':
      return 'FOREST_050';
    case 'wine':
      return 'WINE_050';
    case 'lilac':
      return 'WINE_050';
    case 'rose':
      return 'ROSE_050';
    case 'blush':
      return 'ROSE_050';
    case 'marine':
      return 'MARINE_050';
    case 'sky':
      return 'MARINE_050';
    case 'bay':
      return 'BAY_050';
    case 'cloud':
      return 'BAY_050';
    case 'black':
    default:
      return 'BLACK_030';
  }
};

/**
 * Get the primary hover color for a given colorway
 * @param colorway Button colorway
 * @returns The primary hover button color
 */
const getPrimaryHoverColorway = (colorway: ButtonColorway): string => {
  switch (colorway) {
    case 'forest':
      return 'FOREST_085';
    case 'mint':
      return 'MINT_050';
    case 'wine':
      return 'WINE_085';
    case 'lilac':
      return 'LILAC_050';
    case 'rose':
      return 'ROSE_085';
    case 'blush':
      return 'BLUSH_050';
    case 'marine':
      return 'MARINE_085';
    case 'sky':
      return 'SKY_050';
    case 'bay':
      return 'BAY_085';
    case 'cloud':
      return 'CLOUD_005';
    case 'black':
    default:
      return 'BLACK_075';
  }
};

/**
 * Get the secondary hover color for a given colorway
 * @param colorway Button colorway
 * @returns The secondary hover button color
 */
const getSecondaryHoverColorway = (colorway: ButtonColorway): string => {
  switch (colorway) {
    case 'forest':
      return 'MINT_050';
    case 'mint':
      return 'FOREST_085';
    case 'wine':
      return 'LILAC_050';
    case 'lilac':
      return 'WINE_085';
    case 'rose':
      return 'BLUSH_050';
    case 'blush':
      return 'ROSE_085';
    case 'marine':
      return 'SKY_050';
    case 'sky':
      return 'MARINE_085';
    case 'bay':
      return 'CLOUD_005';
    case 'cloud':
      return 'BAY_085';
    case 'black':
    default:
      return 'BLACK_005';
  }
};

const primaryButtonCss = (colorway: ButtonColorway) => {
  const colorwayEmotion = colorway.toUpperCase() as ColorwayEmotion;
  const complementaryColorwayEmotion = getComplementaryColorway(
    colorway
  ).toUpperCase() as ColorwayEmotion;

  return css`
    background-color: ${COLORS_BUTTONS[`${colorwayEmotion}_100` as keyof typeof COLORS_BUTTONS]};
    color: ${COLORS_BUTTONS[`${complementaryColorwayEmotion}_100` as keyof typeof COLORS_BUTTONS]};
    :hover {
      color: ${COLORS_BUTTONS[
        `${complementaryColorwayEmotion}_100` as keyof typeof COLORS_BUTTONS
      ]};
      background-color: ${COLORS_BUTTONS[
        getPrimaryHoverColorway(colorway) as keyof typeof COLORS_BUTTONS
      ]};
    }
    :active,
    :focus {
      color: ${COLORS_BUTTONS[
        `${complementaryColorwayEmotion}_100` as keyof typeof COLORS_BUTTONS
      ]};
      background-color: ${COLORS_BUTTONS[`${colorwayEmotion}_100` as keyof typeof COLORS_BUTTONS]};
    }
    :disabled {
      color: ${COLORS_BUTTONS[
        `${complementaryColorwayEmotion}_100` as keyof typeof COLORS_BUTTONS
      ]};
      background-color: ${COLORS_BUTTONS[
        getDisabledColorway(colorway) as keyof typeof COLORS_BUTTONS
      ]};
    }
  `;
};

const secondaryButtonCss = (colorway: ButtonColorway) => {
  const colorwayEmotion: ColorwayEmotion = colorway.toUpperCase() as ColorwayEmotion;
  const complementaryColorwayEmotion = getComplementaryColorway(
    colorway
  ).toUpperCase() as ColorwayEmotion;
  const backgroundColor =
    colorwayEmotion === 'BLACK'
      ? COLORS_BUTTONS.WHITE_100
      : COLORS_BUTTONS[`${complementaryColorwayEmotion}_100` as keyof typeof COLORS_BUTTONS];
  const disabledBackgroundColor =
    colorwayEmotion === 'BLACK'
      ? COLORS_BUTTONS.WHITE_100
      : COLORS_BUTTONS[`${complementaryColorwayEmotion}_100` as keyof typeof COLORS_BUTTONS];

  return css`
    background-color: ${backgroundColor};
    border-color: ${COLORS_BUTTONS[`${colorwayEmotion}_100` as keyof typeof COLORS_BUTTONS]};
    color: ${COLORS_BUTTONS[`${colorwayEmotion}_100` as keyof typeof COLORS_BUTTONS]};
    :hover {
      background-color: ${COLORS_BUTTONS[
        getSecondaryHoverColorway(colorway) as keyof typeof COLORS_BUTTONS
      ]};
      color: ${COLORS_BUTTONS[`${colorwayEmotion}_100` as keyof typeof COLORS_BUTTONS]};
    }
    :active,
    :focus {
      background-color: ${backgroundColor};
      color: ${COLORS_BUTTONS[`${colorwayEmotion}_100` as keyof typeof COLORS_BUTTONS]};
    }
    :disabled {
      background-color: ${disabledBackgroundColor};
      border-color: ${COLORS_BUTTONS[getDisabledColorway(colorway) as keyof typeof COLORS_BUTTONS]};
      color: ${COLORS_BUTTONS[getDisabledColorway(colorway) as keyof typeof COLORS_BUTTONS]};
    }
  `;
};

// Secondary Button Danger
const tertiaryButtonCss = () => css`
  background-color: ${COLORS.WHITE_100};
  border-color: ${COLORS_SEMANTIC.NEGATIVE_100};
  color: ${COLORS_SEMANTIC.NEGATIVE_100};
  :hover {
    background-color: ${COLORS_SEMANTIC.NEGATIVE_005};
    color: ${COLORS_SEMANTIC.NEGATIVE_100};
  }
  :active,
  :focus {
    border-color: ${COLORS_SEMANTIC.NEGATIVE_100};
    color: ${COLORS_SEMANTIC.NEGATIVE_100};
  }
  :disabled {
    background-color: ${COLORS_SEMANTIC.NEGATIVE_005};
    border-color: ${COLORS_SEMANTIC.NEGATIVE_030};
    color: ${COLORS_SEMANTIC.NEGATIVE_030};
  }
`;

const transparentButtonCss = () =>
  css`
    background-color: transparent;
    color: ${COLORS.WHITE_100};
    border-color: ${COLORS.WHITE_100};
    :disabled {
      background-color: ${COLORS.BLACK_005};
      color: ${COLORS.BLACK_030};
    }
  `;

// Primary Button Danger
const dangerButtonCss = () =>
  css`
    background-color: ${COLORS_SEMANTIC.NEGATIVE_100};
    color: ${COLORS.WHITE_100};
    :hover {
      background-color: ${COLORS_SEMANTIC.NEGATIVE_075};
      color: ${COLORS.WHITE_100};
    }
    :active,
    :focus {
      background-color: ${COLORS_SEMANTIC.NEGATIVE_100};
      color: ${COLORS.WHITE_100};
    }
    :disabled {
      background-color: ${COLORS_SEMANTIC.NEGATIVE_030};
      color: ${COLORS.WHITE_100};
    }
  `;

export const ButtonWrapper = styled.button`
  border: 1px solid transparent;
  border-radius: 100px;
  text-align: center;
  cursor: pointer;
  font-size: ${FONT.FONT_SIZE_SMALL};
  font-weight: ${FONT.FONT_WEIGHT_SEMI_BOLD};
  height: 44px;
  padding-left: 16px;
  padding-right: 16px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  transition: background-color 0.3s ease;
  ${(props: ButtonProps) =>
    (props.compact || props.size === 'small') &&
    `
      height: 38px;
      font-size:${FONT.FONT_SIZE_SMALLER};
    `}
  ${(props: ButtonProps) =>
    props.size === 'large' &&
    `
        height: 56px;
        padding: 16px 24px;
      `}
  ${(props: ButtonProps) => props.fullWidth && `width: 100%; flex-basis: 100%;`}
  ${(props: ButtonProps) => props.disabled && `cursor: default; box-shadow: none;`}
  :focus-visible {
    outline: 5px auto -webkit-focus-ring-color;
  }
  ${(props) =>
    props.as === 'a' &&
    `
  justify-content: center;
  align-items: center;
  user-select: none;
  text-align: center;
  text-decoration: none;
  &:active,
  &:focus,
  &:hover {
    text-decoration: none;
  }
  `}
  ${(props: ButtonProps) =>
    props.role === 'link' &&
    `
    box-shadow: none;
    background-color: transparent;
    border-radius: unset;
  `}
  ${(props: ButtonProps) => {
    switch (props.variant) {
      case 'primary':
        return primaryButtonCss(props.colorway);
      case 'secondary':
        return secondaryButtonCss(props.colorway);
      case 'tertiary':
        return tertiaryButtonCss();
      case 'transparent':
        return transparentButtonCss();
      case 'danger':
        return dangerButtonCss();
      default:
        return primaryButtonCss(props.colorway);
    }
  }}
`;
